import { useState } from "react";
import imageCompression from "browser-image-compression";
import { Camera, Loader2, Upload, X } from "lucide-react";
import { useApi } from "../api";
import { isNetworkError, saveLocalBlob } from "../lib/outbox";

export type UploadedImage = {
  fileId: string;
  label: string;
  previewUrl: string;
  originalKb: number;
  compressedKb: number;
  /** Photo is stored on this device (no connection); uploaded when the checklist is sent. */
  pending?: boolean;
};

// Aggressive compression tuned for ambulance documentation photos: small files
// that still clearly show the vehicle/equipment. WebP gives the best ratio.
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.06,
  maxWidthOrHeight: 1024,
  initialQuality: 0.5,
  maxIteration: 15,
  useWebWorker: true,
  fileType: "image/webp",
  alwaysKeepResolution: false,
} as const;

/**
 * Photo slot. Upload flow: compress in the browser -> reserve an upload (API) ->
 * PUT straight to object storage -> ask the API to verify it. Only a verified
 * file id is kept, so the checklist can never reference an unchecked upload.
 */
export function ImageUploader({ label, value, onChange }: { label: string; value: UploadedImage | null; onChange: (img: UploadedImage | null) => void }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      const originalKb = Math.round(file.size / 1024);
      // Compression runs in a Web Worker (useWebWorker) so the form stays responsive.
      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
      const compressedKb = Math.round(compressed.size / 1024);
      const previewUrl = URL.createObjectURL(compressed);
      const keepOnDevice = async () => {
        const localId = await saveLocalBlob(compressed);
        onChange({ fileId: localId, label, previewUrl, originalKb, compressedKb, pending: true });
      };
      if (!navigator.onLine) return await keepOnDevice();
      try {
        const { fileId, uploadUrl } = await api.post<{ fileId: string; uploadUrl: string }>("/api/v1/files/uploads", { mimeType: compressed.type, sizeBytes: compressed.size });
        const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": compressed.type }, body: compressed });
        if (!put.ok) throw new Error("Upload failed");
        const verified = await api.post<{ id: string; status: string; url: string | null }>(`/api/v1/files/${fileId}/complete`);
        if (verified.status !== "verified") throw new Error("The photo could not be verified");
        onChange({ fileId, label, previewUrl, originalKb, compressedKb });
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        await keepOnDevice();
      }
    } catch (e) {
      setError((e as Error).message ?? "Failed to process image");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-xl p-3 hover:border-[var(--hmg-red)] transition relative bg-white">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</div>
      {value ? (
        <div className="relative">
          {value.previewUrl ? (
            <img src={value.previewUrl} alt={label} className="w-full aspect-[4/3] object-cover rounded-lg" />
          ) : (
            <div className="w-full aspect-[4/3] rounded-lg bg-slate-100 flex flex-col items-center justify-center gap-1 text-slate-500 text-xs">
              <Camera className="w-6 h-6" strokeWidth={1.5} />
              Photo saved
            </div>
          )}
          {value.pending && <span className="absolute bottom-1 left-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">Saved on device</span>}
          <button type="button" onClick={() => onChange(null)} className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow" aria-label="Remove image">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ) : (
        <label className="block aspect-[4/3] rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer flex flex-col items-center justify-center text-center text-slate-500 text-sm transition">
          {busy ? (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-[var(--hmg-red)] mb-1" />
              <span>Compressing & uploading…</span>
            </>
          ) : (
            <>
              <div className="flex gap-2 mb-1">
                <Camera className="w-7 h-7" strokeWidth={1.5} />
                <Upload className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <span className="text-xs">Click to capture / upload</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
          />
        </label>
      )}
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </div>
  );
}
